
import React, { useState, useEffect } from 'react';
import { IconTarget, IconZap, IconQuant, IconOps } from '../components/Icons';
import { analyzeBestExecution } from '../services/geminiService';
import { useAiQ } from '../state/AiQState';
import toast from 'react-hot-toast';

interface Quote {
    bank: string;
    bid: number;
    offer: number;
    status: 'Streaming' | 'Expired' | 'Hit' | 'Missed';
}

interface RFQ {
    id: string;
    product: string;
    side: 'Buy' | 'Sell';
    quantity: number;
    status: 'Active' | 'Done' | 'Expired';
    quotes: Quote[];
    timeLeft: number;
}

interface Execution {
    id: string;
    product: string;
    side: 'Buy' | 'Sell';
    quantity: number;
    bank: string;
    price: number;
    timestamp: string;
}

const INSTRUMENTS = [
    { group: 'Rates', items: ['USD 2Y Swap', 'USD 5Y Swap', 'USD 10Y Swap', 'USD 30Y Swap', 'EUR 5Y Swap', 'EUR 10Y Swap', 'GBP 10Y Swap'] },
    { group: 'FX', items: ['USD/JPY Spot', 'EUR/USD Spot', 'GBP/USD Spot', 'USD/CNH Spot', 'AUD/USD Spot'] },
    { group: 'Treasuries', items: ['US Treasury 2Y', 'US Treasury 5Y', 'US Treasury 10Y', 'US Treasury 30Y'] },
    { group: 'Credit', items: ['IG CDS 5Y', 'HY CDS 5Y', 'ITRAXX Europe 5Y'] },
    { group: 'Equities', items: ['S&P 500 Future', 'NASDAQ Future', 'EURO STOXX 50 Future'] },
];

const ALL_BANKS = ['J.P. Morgan', 'Goldman Sachs', 'Citi', 'Morgan Stanley', 'Barclays', 'Deutsche Bank', 'UBS'];

const RFQView: React.FC = () => {
    const { addTrade } = useAiQ();
    const [activeTab, setActiveTab] = useState<'rfq' | 'history'>('rfq');
    const [rfqs, setRfqs] = useState<RFQ[]>([]);
    const [executions, setExecutions] = useState<Execution[]>([]);
    const [product, setProduct] = useState('USD 10Y Swap');
    const [quantity, setQuantity] = useState(10_000_000);
    const [side, setSide] = useState<'Buy' | 'Sell'>('Buy');
    const [numBanks, setNumBanks] = useState(4);
    const [aiRecommendation, setAiRecommendation] = useState('');

    // Tick timer
    useEffect(() => {
        const interval = setInterval(() => {
            setRfqs(prev => prev.map(rfq => {
                if (rfq.status !== 'Active') return rfq;
                const newTime = rfq.timeLeft - 1;
                return {
                    ...rfq,
                    timeLeft: newTime,
                    status: newTime <= 0 ? 'Expired' : 'Active',
                    quotes: newTime <= 0 ? rfq.quotes.map(q => ({ ...q, status: 'Expired' as const })) : rfq.quotes,
                };
            }));
        }, 1000);
        return () => clearInterval(interval);
    }, []);

    // Simulate streaming quotes
    useEffect(() => {
        const interval = setInterval(() => {
            setRfqs(prev => prev.map(rfq => {
                if (rfq.status !== 'Active') return rfq;
                const newQuotes = rfq.quotes.map(q => {
                    if (q.status !== 'Streaming') return q;
                    if (Math.random() > 0.7) {
                        const spread = 0.02;
                        const move = (Math.random() - 0.5) * 0.01;
                        const mid = (q.bid + q.offer) / 2 + move;
                        return { ...q, bid: Number((mid - spread / 2).toFixed(4)), offer: Number((mid + spread / 2).toFixed(4)) };
                    }
                    return q;
                });
                return { ...rfq, quotes: newQuotes };
            }));
        }, 800);
        return () => clearInterval(interval);
    }, []);

    const handleCreateRFQ = async () => {
        const newId = `RFQ-${String(Math.floor(Math.random() * 90000) + 10000)}`;
        const basePrice = 4.25;
        const selectedBanks = ALL_BANKS.slice(0, numBanks);
        const initialQuotes: Quote[] = selectedBanks.map(bank => ({
            bank,
            bid: Number((basePrice - 0.01 - Math.random() * 0.01).toFixed(4)),
            offer: Number((basePrice + 0.01 + Math.random() * 0.01).toFixed(4)),
            status: 'Streaming',
        }));

        const newRfq: RFQ = { id: newId, product, side, quantity, status: 'Active', quotes: initialQuotes, timeLeft: 30 };
        setRfqs(prev => [newRfq, ...prev]);
        setAiRecommendation('Analysing quotes…');

        const rec = await analyzeBestExecution(initialQuotes, basePrice);
        setAiRecommendation(rec);
    };

    const handleExecute = (rfqId: string, bank: string, price: number) => {
        setRfqs(prev => prev.map(rfq => {
            if (rfq.id !== rfqId) return rfq;
            return {
                ...rfq,
                status: 'Done',
                quotes: rfq.quotes.map(q => q.bank === bank ? { ...q, status: 'Hit' as const } : { ...q, status: 'Missed' as const }),
            };
        }));

        const rfq = rfqs.find(r => r.id === rfqId);
        if (rfq) {
            const exec: Execution = {
                id: rfq.id,
                product: rfq.product,
                side: rfq.side,
                quantity: rfq.quantity,
                bank,
                price,
                timestamp: new Date().toLocaleTimeString(),
            };
            setExecutions(prev => [exec, ...prev]);

            addTrade({
                id: `TRD-${Math.floor(Math.random() * 10000)}`,
                product: rfq.product,
                counterparty: bank,
                notional: rfq.quantity,
                currency: 'USD',
                valuation: 0,
                status: 'Pending',
                tradeDate: new Date().toISOString().split('T')[0],
                settlementDate: new Date(Date.now() + 172800000).toISOString().split('T')[0],
                trader: 'YOU',
            });
            toast.success(`Executed ${rfq.side} ${rfq.product} with ${bank} @ ${price}`);
        }
    };

    const fmtNotional = (n: number) =>
        n >= 1_000_000_000 ? `${(n / 1_000_000_000).toFixed(1)}B` : `${(n / 1_000_000).toFixed(0)}M`;

    return (
        <div className="flex flex-col h-full bg-[#F0F2F5]">
            {/* ── Tabs ── */}
            <div className="bg-white border-b border-slate-200 px-6 flex items-center gap-1 shrink-0">
                {([['rfq', 'Live RFQ Board'], ['history', 'Execution History']] as const).map(([tab, label]) => (
                    <button
                        key={tab}
                        onClick={() => setActiveTab(tab)}
                        className={`px-4 py-3.5 text-sm font-semibold border-b-2 transition-colors ${
                            activeTab === tab
                                ? 'border-blue-500 text-blue-600'
                                : 'border-transparent text-slate-500 hover:text-slate-800'
                        }`}
                    >
                        {label}
                        {tab === 'history' && executions.length > 0 && (
                            <span className="ml-2 bg-blue-100 text-blue-700 text-[10px] font-bold px-1.5 py-0.5 rounded-full">
                                {executions.length}
                            </span>
                        )}
                    </button>
                ))}
            </div>

            <div className="flex flex-1 overflow-hidden">
                {/* ── Left panel: order form ── */}
                <div className="w-72 bg-white border-r border-slate-200 flex flex-col p-5 shrink-0 overflow-y-auto">
                    <h2 className="text-base font-bold text-slate-800 flex items-center gap-2 mb-1">
                        <IconTarget /> New RFQ
                    </h2>
                    <p className="text-xs text-slate-400 mb-5">Multi-bank request for quote</p>

                    <div className="space-y-4">
                        <div>
                            <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">Instrument</label>
                            <select
                                value={product}
                                onChange={e => setProduct(e.target.value)}
                                className="w-full border border-slate-300 px-3 py-2 rounded-lg bg-white text-sm outline-none focus:border-blue-400"
                            >
                                {INSTRUMENTS.map(grp => (
                                    <optgroup key={grp.group} label={grp.group}>
                                        {grp.items.map(item => (
                                            <option key={item}>{item}</option>
                                        ))}
                                    </optgroup>
                                ))}
                            </select>
                        </div>

                        <div>
                            <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">Side</label>
                            <div className="flex bg-slate-100 p-1 rounded-lg">
                                <button onClick={() => setSide('Buy')} className={`flex-1 py-1.5 rounded-md text-xs font-bold transition-all ${side === 'Buy' ? 'bg-blue-600 text-white shadow' : 'text-slate-500'}`}>BUY</button>
                                <button onClick={() => setSide('Sell')} className={`flex-1 py-1.5 rounded-md text-xs font-bold transition-all ${side === 'Sell' ? 'bg-red-600 text-white shadow' : 'text-slate-500'}`}>SELL</button>
                            </div>
                        </div>

                        <div>
                            <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">Notional (USD)</label>
                            <input
                                type="number"
                                value={quantity}
                                onChange={e => setQuantity(Number(e.target.value))}
                                step={1_000_000}
                                className="w-full border border-slate-300 px-3 py-2 rounded-lg text-sm outline-none focus:border-blue-400 font-mono"
                            />
                            <div className="text-xs text-slate-400 mt-1">{fmtNotional(quantity)} notional</div>
                        </div>

                        <div>
                            <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">Banks ({numBanks})</label>
                            <input
                                type="range"
                                min={2}
                                max={ALL_BANKS.length}
                                value={numBanks}
                                onChange={e => setNumBanks(Number(e.target.value))}
                                className="w-full accent-blue-600"
                            />
                            <div className="text-[10px] text-slate-400 mt-0.5 truncate">
                                {ALL_BANKS.slice(0, numBanks).join(' · ')}
                            </div>
                        </div>

                        <button
                            onClick={handleCreateRFQ}
                            className="w-full py-2.5 bg-slate-900 text-white text-sm font-bold rounded-lg shadow hover:bg-slate-800 transition-all flex items-center justify-center gap-2"
                        >
                            <IconZap /> Send RFQ
                        </button>
                    </div>

                    {/* AI Best-Ex */}
                    <div className="mt-5 p-3.5 bg-violet-50 rounded-lg border border-violet-100 flex-1">
                        <div className="flex items-center gap-1.5 text-violet-800 font-bold text-[11px] uppercase tracking-wider mb-2">
                            <IconQuant /> Best Ex Analysis
                        </div>
                        <p className="text-xs text-slate-700 leading-relaxed min-h-[50px]">
                            {aiRecommendation || 'Send an RFQ to receive AI best-execution analysis.'}
                        </p>
                    </div>
                </div>

                {/* ── Right panel ── */}
                <div className="flex-1 overflow-y-auto p-5 space-y-4">
                    {activeTab === 'rfq' ? (
                        rfqs.length === 0 ? (
                            <div className="flex flex-col items-center justify-center h-full text-slate-400 opacity-50 gap-3">
                                <IconTarget />
                                <p className="text-sm">No active RFQs — send one from the panel on the left</p>
                            </div>
                        ) : (
                            rfqs.map(rfq => (
                                <div key={rfq.id} className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
                                    <div className="bg-slate-50 px-4 py-3 border-b border-slate-100 flex justify-between items-center">
                                        <div className="flex items-center gap-2">
                                            <span className={`text-[11px] font-bold px-2 py-0.5 rounded text-white ${rfq.side === 'Buy' ? 'bg-blue-500' : 'bg-red-500'}`}>
                                                {rfq.side.toUpperCase()}
                                            </span>
                                            <span className="font-bold text-slate-800 text-sm">{rfq.product}</span>
                                            <span className="text-xs text-slate-400 font-mono">{fmtNotional(rfq.quantity)}</span>
                                            <span className="text-[10px] text-slate-400 font-mono">{rfq.id}</span>
                                        </div>
                                        <div className="flex items-center gap-3">
                                            <span className={`font-mono text-sm font-bold ${rfq.status === 'Active' ? 'text-amber-500' : 'text-slate-400'}`}>
                                                {rfq.status === 'Active' ? `${rfq.timeLeft}s` : '—'}
                                            </span>
                                            <span className={`text-[11px] font-bold uppercase px-2 py-0.5 rounded ${
                                                rfq.status === 'Active' ? 'bg-emerald-100 text-emerald-700' :
                                                rfq.status === 'Done' ? 'bg-blue-100 text-blue-700' : 'bg-slate-200 text-slate-500'
                                            }`}>{rfq.status}</span>
                                        </div>
                                    </div>

                                    <div className={`grid divide-x divide-slate-100`} style={{ gridTemplateColumns: `repeat(${rfq.quotes.length}, minmax(0, 1fr))` }}>
                                        {rfq.quotes.map((q, idx) => (
                                            <div key={idx} className={`p-4 flex flex-col items-center transition-colors ${q.status === 'Hit' ? 'bg-emerald-50' : q.status === 'Missed' ? 'bg-slate-50 opacity-50' : ''}`}>
                                                <div className="text-[11px] font-bold text-slate-500 mb-1.5">{q.bank}</div>
                                                <div className="text-lg font-mono font-bold text-slate-800 mb-0.5">
                                                    {rfq.side === 'Buy' ? q.offer.toFixed(4) : q.bid.toFixed(4)}
                                                </div>
                                                <div className="text-[10px] text-slate-400 font-mono mb-2">
                                                    {q.bid.toFixed(4)} / {q.offer.toFixed(4)}
                                                </div>
                                                {rfq.status === 'Active' && (
                                                    <button
                                                        onClick={() => handleExecute(rfq.id, q.bank, rfq.side === 'Buy' ? q.offer : q.bid)}
                                                        className={`px-3 py-1 rounded text-xs font-bold text-white shadow-sm transition-all active:scale-95 ${
                                                            rfq.side === 'Buy' ? 'bg-blue-600 hover:bg-blue-700' : 'bg-red-600 hover:bg-red-700'
                                                        }`}
                                                    >
                                                        {rfq.side === 'Buy' ? 'LIFT' : 'HIT'}
                                                    </button>
                                                )}
                                                {q.status === 'Hit' && <span className="text-[11px] font-bold text-emerald-600 mt-1">EXECUTED</span>}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))
                        )
                    ) : (
                        /* Execution History */
                        executions.length === 0 ? (
                            <div className="flex flex-col items-center justify-center h-full text-slate-400 opacity-50 gap-3">
                                <IconOps />
                                <p className="text-sm">No executions yet</p>
                            </div>
                        ) : (
                            <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                                <div className="px-5 py-3 border-b border-slate-100">
                                    <span className="text-sm font-bold text-slate-700">Execution Log</span>
                                    <span className="text-xs text-slate-400 ml-2">{executions.length} trades today</span>
                                </div>
                                <table className="w-full text-sm">
                                    <thead className="bg-slate-50 border-b border-slate-100">
                                        <tr>
                                            {['Time', 'RFQ ID', 'Product', 'Side', 'Notional', 'Bank', 'Price'].map(h => (
                                                <th key={h} className="px-4 py-2.5 text-left text-xs font-bold text-slate-500 uppercase tracking-wider">{h}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100">
                                        {executions.map(ex => (
                                            <tr key={ex.id} className="hover:bg-slate-50 transition-colors">
                                                <td className="px-4 py-3 text-xs font-mono text-slate-400">{ex.timestamp}</td>
                                                <td className="px-4 py-3 text-xs font-mono text-slate-500">{ex.id}</td>
                                                <td className="px-4 py-3 font-medium text-slate-800">{ex.product}</td>
                                                <td className="px-4 py-3">
                                                    <span className={`text-xs font-bold px-2 py-0.5 rounded ${ex.side === 'Buy' ? 'bg-blue-100 text-blue-700' : 'bg-red-100 text-red-600'}`}>
                                                        {ex.side.toUpperCase()}
                                                    </span>
                                                </td>
                                                <td className="px-4 py-3 font-mono text-slate-700">{fmtNotional(ex.quantity)}</td>
                                                <td className="px-4 py-3 text-slate-700">{ex.bank}</td>
                                                <td className="px-4 py-3 font-mono font-semibold text-slate-900">{ex.price.toFixed(4)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )
                    )}
                </div>
            </div>
        </div>
    );
};

export default RFQView;
